import { useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase';
import { errorMessage } from '../lib/format';
import { useTable } from '../lib/useTable';
import type { Instructor } from '../lib/types';
import { PageHead } from '../components/AppLayout';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';

export function InstructorsPage() {
  const { rows, loading, error, reload } = useTable<Instructor>('instructors', 'name');
  const toast = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<Instructor | null>(null);
  const [creating, setCreating] = useState(false);

  async function remove(row: Instructor) {
    const ok = await confirm({
      title: `Remove instructor "${row.name}"?`,
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    const { error } = await supabase.from('instructors').delete().eq('id', row.id);
    if (error) {
      toast.error(
        error.code === '23503'
          ? `${row.name} is assigned to scheduled classes — reassign or delete those classes first.`
          : errorMessage(error),
      );
    } else {
      toast.success(`${row.name} removed.`);
      void reload();
    }
  }

  return (
    <>
      <PageHead
        title="Instructors"
        subtitle="People who run your classes. Saved here so you can pick them when scheduling."
        action={
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            + Add instructor
          </button>
        }
      />

      <div className="card">
        {error && <p className="error-text">{error}</p>}
        {loading ? (
          <p className="muted">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="empty">
            <span className="empty-emoji">🧑‍🏫</span>
            No instructors yet. Add your first one to get started.
          </div>
        ) : (
          <table className="table cards">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td style={{ fontWeight: 600 }}>{row.name}</td>
                  <td className="muted" data-label="Email">
                    {row.email || '—'}
                  </td>
                  <td className="muted" data-label="Phone">
                    {row.phone || '—'}
                  </td>
                  <td>
                    <div className="row" style={{ justifyContent: 'flex-end' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditing(row)}>
                        Edit
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => void remove(row)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(creating || editing) && (
        <InstructorForm
          instructor={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            void reload();
          }}
        />
      )}
    </>
  );
}

function InstructorForm({
  instructor,
  onClose,
  onSaved,
}: {
  instructor: Instructor | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(instructor?.name ?? '');
  const [email, setEmail] = useState(instructor?.email ?? '');
  const [phone, setPhone] = useState(instructor?.phone ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const payload = {
      name: name.trim(),
      email: email.trim() || null,
      phone: phone.trim() || null,
    };
    const query = instructor
      ? supabase.from('instructors').update(payload).eq('id', instructor.id)
      : supabase.from('instructors').insert(payload);
    const { error } = await query;
    setBusy(false);
    if (error) setErr(errorMessage(error));
    else onSaved();
  }

  return (
    <Modal title={instructor ? 'Edit instructor' : 'Add instructor'} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="i-name">Name</label>
          <input
            id="i-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="i-email">Email (optional)</label>
            <input
              id="i-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="i-phone">Phone (optional)</label>
            <input id="i-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
        </div>
        {err && <p className="error-text">{err}</p>}
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: '0.6rem' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy || !name.trim()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
